/**
 * Shared two-option export dropdown trigger.
 *
 * Reuses the existing inline anchored-menu pattern (see GoalDetailScreen's
 * comment menu, ~1857-1925) as ONE shared plain component instead of one-off
 * copies per screen. This is NOT a design-system component — it lives in
 * `src/components/`, not `src/design/components/`.
 *
 * Renders the menu inside a transparent `Modal` rather than as an absolutely
 * positioned sibling in the header row: on React Native Web, `position:
 * absolute` + `zIndex` only wins within its own stacking context, and later
 * DOM siblings (e.g. the page's card list) can still paint on top of it. A
 * `Modal` renders through RN Web's app-root portal, above all in-page
 * content, sidestepping the stacking-context issue entirely. The trigger's
 * on-screen position is measured via `measureInWindow` when the menu opens
 * so the modal-rendered menu can still be anchored under the button.
 */
import React, { useRef, useState } from 'react';
import {
  Dimensions,
  Modal,
  Pressable,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';

import { tokens } from '@design';
import { Button } from '@design/components/Button';
import { FONTS } from '../constants/config';
import { useLanguage } from '../contexts/LanguageContext';
import { ChevronDownIcon } from './Icons';

export interface ExportMenuButtonProps {
  /** Existing data export (unchanged behaviour). */
  onExportData: () => void;
  /** New performance-results export. */
  onExportResults: () => void;
  /** Disables the trigger and shows a spinner while either export is running. */
  isExporting: boolean;
}

const MENU_WIDTH = 180;
const MENU_GAP = tokens.spacing.absolute['1'];
const VIEWPORT_MARGIN = tokens.spacing.absolute['2'];

interface AnchorRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export function ExportMenuButton({
  onExportData,
  onExportResults,
  isExporting,
}: ExportMenuButtonProps) {
  const { t, isRTL } = useLanguage();
  const [menuVisible, setMenuVisible] = useState(false);
  const [anchor, setAnchor] = useState<AnchorRect | null>(null);
  const triggerRef = useRef<View>(null);

  const openMenu = () => {
    triggerRef.current?.measureInWindow((x: number, y: number, width: number, height: number) => {
      setAnchor({ x, y, width, height });
      setMenuVisible(true);
    });
  };

  const closeMenu = () => setMenuVisible(false);

  const pick = (handler: () => void) => {
    closeMenu();
    handler();
  };

  // RTL-safe horizontal placement: align the menu's leading edge (start)
  // with the trigger's leading edge in both directions, then clamp so it
  // never overflows the viewport's left or right edge.
  const windowWidth = Dimensions.get('window').width;
  let menuLeft = anchor ? (isRTL ? anchor.x + anchor.width - MENU_WIDTH : anchor.x) : 0;
  menuLeft = Math.max(
    VIEWPORT_MARGIN,
    Math.min(menuLeft, windowWidth - MENU_WIDTH - VIEWPORT_MARGIN)
  );
  const menuTop = anchor ? anchor.y + anchor.height + MENU_GAP : 0;

  return (
    <View ref={triggerRef} collapsable={false} style={styles.wrapper}>
      <Button
        variant="secondary"
        loading={isExporting}
        trailingIcon={
          <ChevronDownIcon size={16} color={tokens.colors.semantic.general.foreground} />
        }
        onPress={openMenu}
      >
        {t('common.exportExcel')}
      </Button>
      <Modal
        visible={menuVisible}
        transparent
        animationType="none"
        onRequestClose={closeMenu}
      >
        <Pressable style={styles.backdrop} onPress={closeMenu}>
          {/*
            `left` here is a physical (not logical) window coordinate from
            `measureInWindow`, already RTL-corrected above via `isRTL` — using
            the logical `start` key would make RN Web apply a second,
            conflicting RTL flip on top of the manual one.
          */}
          {/* eslint-disable-next-line no-restricted-syntax */}
          <View style={[styles.dropdown, { top: menuTop, left: menuLeft }]}>
            <TouchableOpacity style={styles.item} onPress={() => pick(onExportData)}>
              <Text style={styles.itemText}>{t('common.exportData')}</Text>
            </TouchableOpacity>
            <View style={styles.separator} />
            <TouchableOpacity style={styles.item} onPress={() => pick(onExportResults)}>
              <Text style={styles.itemText}>{t('common.exportResults')}</Text>
            </TouchableOpacity>
          </View>
        </Pressable>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    // No longer needs position/zIndex tricks — the menu itself now renders
    // in a Modal, above all page content, regardless of this wrapper's
    // stacking context.
  },
  // Full-screen transparent backdrop inside the Modal; pressing it (outside
  // the menu) dismisses the menu — also fixes the prior lack of
  // outside-click dismissal.
  backdrop: {
    flex: 1,
  },
  // Matches GoalDetailScreen's commentMenuDropdown (radius/border/shadow):
  // frontend/src/screens/goals/GoalDetailScreen.tsx ~1911-1924
  dropdown: {
    position: 'absolute',
    minWidth: MENU_WIDTH,
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute['radius-12'],
    borderWidth: tokens.spacing.absolute['px'],
    borderColor: tokens.colors.raw.slate['200'],
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.2,
    shadowRadius: 10,
    elevation: 16,
    paddingVertical: tokens.spacing.absolute['3'],
    paddingHorizontal: tokens.spacing.absolute['1'],
  },
  item: {
    justifyContent: 'center',
    minHeight: 44,
    paddingVertical: tokens.spacing.absolute['3'],
    paddingHorizontal: tokens.spacing.absolute['4'],
  },
  itemText: {
    fontFamily: FONTS.regular,
    fontSize: tokens.typography.paragraph['paragraph small font size'],
    color: tokens.colors.semantic.general.foreground,
  },
  separator: {
    height: 1,
    backgroundColor: tokens.colors.raw.slate['200'],
    marginHorizontal: tokens.spacing.absolute['2'],
  },
});
